/**
 * The wire format consumed by mjkm.co.uk/src/components/signed.tsx.
 *
 * The player scales from a 400x240 origin canvas and replays points by their
 * own timestamps, so capture must happen at exactly those dimensions and must
 * preserve real wall-clock timing. Changing either breaks the live animation.
 */
export const CAPTURE_WIDTH = 400;
export const CAPTURE_HEIGHT = 240;

/**
 * Decimal places kept on every coordinate.
 *
 * The 400x240 space is a coordinate system, not a resolution — nothing forces a
 * point onto a whole number, and the original tool rounding to integers is what
 * actually capped fidelity. On a pad the width of an iPad, one integer step is
 * most of a millimetre of real hand movement, so the curve arrived pre-flattened
 * however finely the pen was sampled. Two places puts a step at 1/40000 of the
 * width, far below what a hand or a screen resolves, while staying a plain finite
 * number — all signed.tsx and parseSignatureData ever required. A bigger pad now
 * buys detail rather than coarser quantisation.
 */
export const PRECISION = 2;

/**
 * Two points closer together than this are the same point. The old check compared
 * rounded integers, so it silently discarded any movement under half a unit —
 * most of a slow, careful passage. This sits below the precision floor, so it can
 * only drop a genuinely stationary repeat.
 */
export const MIN_STEP = 0.05;

export function quantise(value: number): number {
  const factor = 10 ** PRECISION;
  return Math.round(value * factor) / factor;
}

/** Colours the site actually renders the signature with, so the pad is a true preview. */
export const PAPER = "#696969";
export const INK = "#e7e6f7";

export interface Point {
  x: number;
  y: number;
  /** Epoch milliseconds. The player derives all timing from these. */
  timestamp: number;
  /** 0–1. Pens report real force; mice have none and are normalised to 1. */
  pressure: number;
  /** Which pen-down this point belongs to. Lets the player avoid bridging pen-lifts. */
  strokeId: number;
}

export interface Stroke {
  points: Point[];
  id: number;
}

export interface SignatureData {
  strokes: Stroke[];
  startTime: number;
  endTime: number;
}

/** Mice report pressure 0.5 while held; the original captures recorded 1. */
export const MOUSE_PRESSURE = 1;

export function normalisePressure(pressure: number, isPen: boolean): number {
  if (!isPen) return MOUSE_PRESSURE;
  // A pen mid-stroke can momentarily report 0; clamp so it never renders as a gap.
  return Math.min(1, Math.max(0.05, pressure));
}

/**
 * Narrowing parse for a signature.json loaded from disk. Deliberately strict:
 * a malformed file should fail loudly here, not render as an empty canvas.
 */
export function parseSignatureData(raw: unknown): SignatureData {
  if (typeof raw !== "object" || raw === null) throw new Error("Not a JSON object");
  const data = raw as Record<string, unknown>;

  if (!Array.isArray(data.strokes)) throw new Error("Missing `strokes` array");
  if (data.strokes.length === 0) throw new Error("`strokes` is empty");

  const strokes: Stroke[] = data.strokes.map((s, i) => {
    const stroke = s as Record<string, unknown>;
    if (!Array.isArray(stroke.points)) throw new Error(`Stroke ${i} has no \`points\``);

    const points: Point[] = stroke.points.map((p, j) => {
      const pt = p as Record<string, unknown>;
      for (const key of ["x", "y", "timestamp"]) {
        if (typeof pt[key] !== "number" || !Number.isFinite(pt[key])) {
          throw new Error(`Stroke ${i}, point ${j}: \`${key}\` is not a finite number`);
        }
      }
      return {
        x: pt.x as number,
        y: pt.y as number,
        timestamp: pt.timestamp as number,
        pressure: typeof pt.pressure === "number" ? pt.pressure : MOUSE_PRESSURE,
        // Older captures grouped several pen-downs under one stroke object and
        // carried the real id on the point, so trust the point first.
        strokeId: typeof pt.strokeId === "number" ? pt.strokeId : i + 1,
      };
    });

    return { points, id: typeof stroke.id === "number" ? stroke.id : i + 1 };
  });

  const all = strokes.flatMap((s) => s.points);
  if (all.length === 0) throw new Error("No points in any stroke");

  const timestamps = all.map((p) => p.timestamp);
  return {
    strokes,
    startTime: typeof data.startTime === "number" ? data.startTime : Math.min(...timestamps),
    endTime: typeof data.endTime === "number" ? data.endTime : Math.max(...timestamps),
  };
}
